import { useMemo } from "react";
import { addDays, isToday, startOfDay, subDays } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, toTimestamp } from "@racoon/lib";

import { isPastDay } from "~/shared/api/cache";
import { useListJobsPastDayQuery, useListJobsQuery } from "~/shared/api/jobs/job.grpc";
import { useListRoutesPastDayQuery, useListRoutesQuery } from "~/shared/api/routes/route.grpc";
import { GRPC_ROUTE_CATEGORY_TO_JOB_CATEGORY } from "~/shared/lib/category-maps";
import { useHeldWhileLoading } from "~/shared/hooks/use-held-while-loading";
import {
  BucketKey,
  DEFAULT_DEADLINE_MS,
  PickupStatus,
  bucketOf,
  isDatedByDeadline,
  jobDeadline,
  summarizePickupStatus,
} from "./pickup-status.lib";
import { SiteJob, byUrgency, jobTone } from "./site-pins";

const S = ConnectProto.JobStatus;

// Unassigned jobs carry no plan_start_at, so they are dated by their deadline instead.
const SCHEDULED_STATUSES = [
  S.CREATED,
  S.ASSIGNED,
  S.ENROUTE,
  S.ON_SERVICE,
  S.COMPLETED,
  S.FORCE_COMPLETED,
  S.NOT_COMPLETED,
];
// The backend marks an overdue unassigned job missed without planning it.
const UNPLANNED_STATUSES = [S.CREATED, S.NOT_COMPLETED];
const RUNNING_STATUSES = [S.ASSIGNED, S.ENROUTE, S.ON_SERVICE];
const NO_JOBS: ConnectProto.Job[] = [];
// How far back today reaches for orders still waiting to be assigned.
const BACKLOG_DAYS = 90;

/** The dashboard's shared day/category/division selection, driving the card and the map. */
export type DashboardFilters = {
  day: Date;
  category: ConnectProto.RouteCategory | "all";
  /** Sub-organization to narrow to; the whole org (with descendants) when null. */
  divisionId: bigint | null;
};

export type PickupStatusFilters = DashboardFilters & { organizationId: number | null };

export type BucketedJob = { job: ConnectProto.Job; bucket: BucketKey };

type DayResult = { summary: PickupStatus; jobs: BucketedJob[]; routes: ConnectProto.Route[] };

const EMPTY: DayResult = {
  summary: summarizePickupStatus([], []),
  jobs: [],
  routes: [],
};

type JobsArgs = Parameters<typeof useListJobsQuery>[0];
type RoutesArgs = Parameters<typeof useListRoutesQuery>[0];
type JobsBase = {
  serviceProviderIds: bigint[];
  jobCategories?: ConnectProto.JobCategory[];
  pickupLocationIds?: bigint[];
};

type QueryHook<A, R> = (args: A, options: { skip: boolean }) => R;

// Same request either way; a past day goes through the long-cache endpoint.
function useDayQuery<A, R>(
  useLive: QueryHook<A, R>,
  usePast: QueryHook<A, R>,
  args: A,
  past: boolean,
  skip: boolean,
) {
  const live = useLive(args, { skip: skip || past });
  const settled = usePast(args, { skip: skip || !past });
  return past ? settled : live;
}

const useDayJobsQuery = (args: JobsArgs, past: boolean, skip: boolean) =>
  useDayQuery(useListJobsQuery, useListJobsPastDayQuery, args, past, skip);

export const useDayRoutes = (args: RoutesArgs, past: boolean, skip: boolean) =>
  useDayQuery(useListRoutesQuery, useListRoutesPastDayQuery, args, past, skip);

export function dayRange(dayKey: number) {
  const start = new Date(dayKey);
  const end = addDays(start, 1);
  // The API's range is inclusive at both ends; jobs scheduled at 00:00 would count on two days.
  return { from: start, to: end, last: new Date(end.getTime() - 1) };
}

/** Jobs dated on the day: scheduled by plan_start_at, unplanned by deadline (tw_end_at, else
 *  created_at + 24h). Today also carries yesterday's still-running plans and overdue orders.
 *  `jobs` stays undefined until every query is in. */
function useDayJobs(base: JobsBase, range: ReturnType<typeof dayRange>, skip: boolean) {
  const { from, last } = range;
  const past = isPastDay(from);
  const today = isToday(from);
  const timeWindow = (field: string, shiftMs = 0) => ({
    from: toTimestamp(new Date(from.getTime() - shiftMs)),
    to: toTimestamp(new Date(last.getTime() - shiftMs)),
    field,
  });
  const scheduled = useDayJobsQuery(
    { ...base, jobStatuses: SCHEDULED_STATUSES, timeFilter: timeWindow("plan_start_at") },
    past,
    skip,
  );
  const unassigned = useDayJobsQuery(
    { ...base, jobStatuses: UNPLANNED_STATUSES, timeFilter: timeWindow("tw_end_at") },
    past,
    skip,
  );
  const undated = useDayJobsQuery(
    {
      ...base,
      jobStatuses: UNPLANNED_STATUSES,
      timeFilter: timeWindow("created_at", DEFAULT_DEADLINE_MS),
    },
    past,
    skip,
  );

  const running = useDayJobsQuery(
    {
      ...base,
      jobStatuses: RUNNING_STATUSES,
      timeFilter: {
        field: "plan_start_at",
        from: toTimestamp(subDays(from, 1)),
        to: toTimestamp(new Date(from.getTime() - 1)),
      },
    },
    false,
    skip || !today,
  );
  const backlog = useDayJobsQuery(
    {
      ...base,
      jobStatuses: [S.CREATED],
      timeFilter: {
        field: "created_at",
        from: toTimestamp(subDays(from, BACKLOG_DAYS)),
        to: toTimestamp(last),
      },
    },
    false,
    skip || !today,
  );

  // currentData, not data: data keeps the previous day's result while the new day loads.
  const a = scheduled.currentData?.jobs;
  const b = unassigned.currentData?.jobs;
  const c = undated.currentData?.jobs;
  // The carry-over is extra: failing, it drops out instead of blanking the day.
  const d = today && !running.isError ? running.currentData?.jobs : NO_JOBS;
  const e = today && !backlog.isError ? backlog.currentData?.jobs : NO_JOBS;
  const jobs = useMemo(() => {
    if (!a || !b || !c || !d || !e) return undefined;
    const byId = new Map<string, ConnectProto.Job>();
    const carried = [
      // A plan from yesterday evening that is still running belongs to today too.
      ...d.filter((j) => {
        const planEnd = fromTimestamp(j.planEndAt);
        return !planEnd || planEnd >= from;
      }),
      // Overdue and still unassigned; one whose window opens later isn't today's work yet.
      ...e.filter((j) => {
        const deadline = jobDeadline(j);
        const windowStart = fromTimestamp(j.twStartAt);
        return deadline && deadline < from && (!windowStart || windowStart <= last);
      }),
    ];
    const byDeadline = [...b, ...c.filter((j) => !j.twEndAt)].filter(isDatedByDeadline);
    for (const j of [...a, ...byDeadline, ...carried]) {
      byId.set(String(j.id), j);
    }
    return [...byId.values()];
  }, [a, b, c, d, e, from, last]);

  return { jobs, isError: scheduled.isError || unassigned.isError || undated.isError };
}

/** The day's jobs in their buckets. Panel and map both call it; RTK shares the requests. */
export function usePickupStatus({
  day,
  category,
  divisionId,
  organizationId,
}: PickupStatusFilters) {
  const dayKey = startOfDay(day).getTime();
  const range = useMemo(() => dayRange(dayKey), [dayKey]);
  const { from, last } = range;

  const providerIds = divisionId ? [divisionId] : organizationId ? [BigInt(organizationId)] : [];
  const jobCategories = category === "all" ? [] : [GRPC_ROUTE_CATEGORY_TO_JOB_CATEGORY[category]];
  const skip = providerIds.length === 0;

  const dayJobs = useDayJobs({ serviceProviderIds: providerIds, jobCategories }, range, skip);
  // Only finished trips have been weighed, so the rest would just be payload.
  const routes = useDayRoutes(
    {
      ownerOrganizationIds: providerIds,
      datetimeFilter: { from: toTimestamp(from), to: toTimestamp(last) },
      routeStatuses: [ConnectProto.RouteStatus.COMPLETED, ConnectProto.RouteStatus.FORCE_COMPLETED],
      routeCategories: category === "all" ? [] : [category],
    },
    isPastDay(from),
    skip,
  );

  const all = dayJobs.jobs;
  // Weight only: the job pins and counts don't wait for it, nor fail with it.
  const dayRoutes = routes.currentData?.routes;
  // Its own memo, so the routes arriving later don't redraw every pin.
  const jobs = useMemo(() => {
    if (!all) return undefined;
    const out: BucketedJob[] = [];
    for (const job of all) {
      const bucket = bucketOf(job);
      if (bucket) out.push({ job, bucket });
    }
    return out;
  }, [all]);
  const fresh = useMemo((): DayResult | undefined => {
    if (!all || !jobs) return undefined;
    const routes = dayRoutes ?? [];
    return { summary: summarizePickupStatus(all, routes), jobs, routes };
  }, [all, jobs, dayRoutes]);

  // Bridges a day switch within one scope; another org, division or category starts empty,
  // and so does a failed load.
  const isError = dayJobs.isError;
  const held = useHeldWhileLoading(fresh, `${organizationId}|${divisionId}|${category}`, isError);

  return { ...(held ?? EMPTY), isFetching: !fresh && !isError, isError };
}

/** One site's jobs on a day, in the same buckets as the dashboard, most urgent first. */
export function useSiteDayJobs(siteId: string, day: Date, organizationId: number | null) {
  const dayKey = startOfDay(day).getTime();
  const range = useMemo(() => dayRange(dayKey), [dayKey]);

  const skip = !organizationId || !/^[1-9]\d*$/.test(siteId);
  const dayJobs = useDayJobs(
    {
      serviceProviderIds: organizationId ? [BigInt(organizationId)] : [],
      pickupLocationIds: skip ? [] : [BigInt(siteId)],
    },
    range,
    skip,
  );

  const all = dayJobs.jobs;
  const jobs = useMemo((): SiteJob[] => {
    const out: SiteJob[] = [];
    for (const job of all ?? []) {
      const bucket = bucketOf(job);
      if (bucket) out.push({ job, bucket, tone: jobTone(job, bucket) });
    }
    return out.sort(byUrgency);
  }, [all]);

  return {
    jobs,
    isLoading: !skip && !all && !dayJobs.isError,
    isError: dayJobs.isError,
  };
}
